/**
 * ConsoleCapture — Singleton ring buffer a console error/warn üzenetekhez.
 * Inicializálás: main.tsx-ben, app bootstrap-kor.
 * Ticket beküldéskor az utolsó N entry-t csatoljuk.
 */

export interface ConsoleEntry {
  level: 'error' | 'warn';
  message: string;
  source?: string;
  line?: number;
  stack?: string;
  timestamp: number;
}

const MAX_ENTRIES = 50;
/** Bájt-plafon (🌐 CR `#2162` 2.): eddig csak a `console.*` út vágott, a `window` hibák nem. */
const MAX_MESSAGE = 300;
const MAX_STACK = 500;
/**
 * A maszk ELŐTTI plafon (a regex költsége miatt) a `sanitize`-ban; a végleges vágás a MASZK
 * UTÁN. Ha a vágás a maszk előtt `300`-nál történne, egy határon kettévágott e-mail-csonk nem
 * illeszkedne a mintára, és maszkolatlanul menne tovább.
 */
const PRE_MASK_CAP = 2000;
/**
 * A gyűjtő-utak NYERS plafonja (a `join`/`String()` költségére). Szándékosan NAGYOBB a
 * `PRE_MASK_CAP`-nál: így a `sanitize` mindig látja, hogy vágás történt-e.
 */
const RAW_CAP = 10_000;

/**
 * T-71-21 (a), 🛡️ F5: a maszk ZSUGORÍT, ezért a `PRE_MASK_CAP`-nál kettévágott utolsó token a
 * végleges kimenetbe csúszhat (≥`45` karakteres, szóközzel tagolt e-mail-soron mérve). Ha vágás
 * történt, a határon álló CSONKA futamot levágjuk: az e-mail/telefon/adószám karakterkészletébe
 * tartozó utolsó, legfeljebb `254` karakteres futamot (254 = az e-mail cím max. hossza).
 * ⚠️ Nem az utolsó szóközig vágunk: egy hosszú, szóköz nélküli tartalom (minifikált JSON,
 * base64) különben TELJESEN elveszne — és vele a hibakeresési érték.
 */
function boundForMask(text: string): string {
  if (text.length <= PRE_MASK_CAP) return text;
  return text.slice(0, PRE_MASK_CAP).replace(/[\w.%+@\-/()]{1,254}$/, '');
}

/**
 * PII-maszkolás a pufferbe írás ELŐTT (ruling 54, 🌐 CR `#2162` 1.). A puffer a helpdesk-jegyhez
 * csatolódik (`HelpdeskDrawer` `getRecent(10)`) és a reload-carry óta a `sessionStorage`-ba is
 * kerül — egy BE-hibaüzenet ügyfél-e-mailt visszhangozhat (`quote-email.service.ts:71`).
 * A CLAUDE.md PII-szabálya: e-mail · telefonszám · adószám nem megy tovább.
 *
 * ⚠️ A minták SZŰKEK szándékosan: a hibakereséshez kellő azonosítók (UUID, sorszám, stack
 * `:sor:oszlop`, összeg) NEM maszkolódnak — ezt a spec negatív esetei rögzítik.
 */
const PII_PATTERNS: ReadonlyArray<[RegExp, string]> = [
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[email]'],
  // magyar adószám: 8-1-2 (az UUID-szegmens 8-4-4 alakú, arra nem illeszkedik)
  [/(?<![\w-])\d{8}-\d-\d{2}(?![\w-])/g, '[adoszam]'],
  // telefon (hazai): `+36` / `0036` / `06` előtag — zárójelben is —, 1-2 jegyű körzet, majd
  // 6-7 jegyű előfizetői szám BÁRMILYEN 2-3-as tagolással (🛡️ F2: `06 30 123 45 67` ·
  // `(06) 30 123 4567` · `(06-30) 123-4567`). Tagoló: szóköz / kötőjel / perjel.
  [
    /(?<![\w+-])\(?(?:\+36|0036|06)\)?[\s/-]?\(?\d{1,2}\)?[\s/-]?\d(?:[\s/-]?\d){5,6}(?![\w-])/g,
    '[telefon]',
  ],
  // nemzetközi: + és legalább 8 számjegy, tagolva vagy anélkül
  [/(?<![\w+-])\+\d{1,3}(?:[\s-]?\d){7,12}(?![\w-])/g, '[telefon]'],
  // adószám kötőjel NÉLKÜL (T-71-21 (a), 🛡️ F7): 8 + ÁFA-kód (1-5) + 2 jegyű megyekód, pontosan
  // 11 jegy. A telefon-minták UTÁN áll, hogy a `06…` hívószám `[telefon]` címkét kapjon.
  [/(?<![\w-])\d{8}[1-5]\d{2}(?![\w-])/g, '[adoszam]'],
];

export function maskPii(text: string): string {
  return PII_PATTERNS.reduce((acc, [re, label]) => acc.replace(re, label), text);
}

/** A puffer EGYETLEN bemeneti szűrője: maszk + plafon. A `push()` és a `readCarry()` is ezt hívja. */
function sanitize(entry: ConsoleEntry): ConsoleEntry {
  return {
    ...entry,
    message: maskPii(boundForMask(entry.message)).substring(0, MAX_MESSAGE),
    ...(entry.source !== undefined && {
      source: maskPii(boundForMask(entry.source)).substring(0, MAX_MESSAGE),
    }),
    ...(entry.stack !== undefined && {
      stack: maskPii(boundForMask(entry.stack)).substring(0, MAX_STACK),
    }),
  };
}

function isConsoleEntry(value: unknown): value is ConsoleEntry {
  if (typeof value !== 'object' || value === null) return false;
  const e = value as Record<string, unknown>;
  return (
    (e['level'] === 'error' || e['level'] === 'warn') &&
    typeof e['message'] === 'string' &&
    typeof e['timestamp'] === 'number' &&
    (e['source'] === undefined || typeof e['source'] === 'string') &&
    (e['stack'] === undefined || typeof e['stack'] === 'string')
  );
}

/**
 * T-71-20 (e) — a teljes újratöltés (pl. a `RouteErrorBoundary` újrapróbája) a memóriabeli
 * puffert TÖRLI, pedig a helpdesk-jegy épp innen csatolná a hibát. A reload ELŐTT a puffer
 * ide kerül, és a következő `init()` visszatölti (egyszer, utána törli a kulcsot).
 */
export const RELOAD_CARRY_KEY = 'kgc.consoleCapture.reloadCarry';

function readCarry(): ConsoleEntry[] {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_CARRY_KEY);
    window.sessionStorage.removeItem(RELOAD_CARRY_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    // 🛡️ CR `#2164` 3.: a visszatöltött elem is a maszkon megy át (egy deploy előtti bundle
    // carry-je maszkolatlan lehet). Az alak-szűrő azért kell, mert a `sanitize` stringet vár.
    return Array.isArray(parsed) ? parsed.filter(isConsoleEntry).map(sanitize) : [];
  } catch {
    return []; // tárolás tiltva / sérült tartalom: a hiba-gyűjtés NEM állhat meg emiatt
  }
}

export class ConsoleCaptureService {
  private entries: ConsoleEntry[] = [];
  private initialized = false;

  init(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.entries = [...readCarry(), ...this.entries].slice(-MAX_ENTRIES);

    // 1. Unhandled JS errors
    window.addEventListener('error', (event: ErrorEvent) => {
      this.push({
        level: 'error',
        message: (event.message || 'Unknown error').substring(0, RAW_CAP),
        source: event.filename,
        line: event.lineno,
        stack: event.error?.stack?.substring(0, RAW_CAP),
        timestamp: Date.now(),
      });
    });

    // 2. Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      this.push({
        level: 'error',
        message: String(reason?.message || String(reason) || 'Unhandled rejection').substring(
          0,
          RAW_CAP
        ),
        stack: reason?.stack?.substring(0, RAW_CAP),
        timestamp: Date.now(),
      });
    });

    // 3. Override console.error
    const origError = console.error;
    console.error = (...args: unknown[]) => {
      this.push({
        level: 'error',
        message: args.map(String).join(' ').substring(0, RAW_CAP),
        timestamp: Date.now(),
      });
      origError.apply(console, args);
    };

    // 4. Override console.warn
    const origWarn = console.warn;
    console.warn = (...args: unknown[]) => {
      this.push({
        level: 'warn',
        message: args.map(String).join(' ').substring(0, RAW_CAP),
        timestamp: Date.now(),
      });
      origWarn.apply(console, args);
    };
  }

  private push(entry: ConsoleEntry): void {
    // A négy gyűjtő-út (window error · unhandledrejection · console.error · console.warn) ide
    // fut, az ötödik bemenet (a reload-carry) a `readCarry()`-ben — mindkettő a `sanitize`-on át.
    this.entries.push(sanitize(entry));
    if (this.entries.length > MAX_ENTRIES) {
      this.entries.shift();
    }
  }

  /** Utolsó N entry lekérdezése (ticket csatoláshoz) */
  getRecent(limit = 10): ConsoleEntry[] {
    return this.entries.slice(-limit);
  }

  /** Összes error (nem warn) száma — badge-hez */
  getErrorCount(): number {
    return this.entries.filter(e => e.level === 'error').length;
  }

  /** Utolsó lekérdezés óta keletkezett error-ök száma */
  getErrorCountSince(timestamp: number): number {
    return this.entries.filter(e => e.level === 'error' && e.timestamp > timestamp).length;
  }

  /** Teljes újratöltés ELŐTT hívandó: a puffer túléli a reloadot (a következő `init()` visszatölti). */
  persistForReload(): void {
    try {
      window.sessionStorage.setItem(RELOAD_CARRY_KEY, JSON.stringify(this.entries));
    } catch {
      // tárolás tiltva / kvóta: a reload ettől még menjen — a hiba a konzolban ott van
    }
  }

  clear(): void {
    this.entries = [];
  }
}

export const consoleCapture = new ConsoleCaptureService();
